import { HttpError, type UnauthorizedReason } from './HTTPErrors';
import { getUnauthorizedReason } from './unauthorizedReason';

const UNAUTHORIZED_ERROR_NAMES = new Set(['Unauthorized']);

const UNAUTHORIZED_STATUS_CODES = new Set([401]);

const UNAUTHORIZED_PATTERNS = [
  'unauthorized',
  '401',
  'session has expired',
] as const;

/**
 * Returns `true` when the error represents an HTTP 401 / unauthorized response.
 * These are expected when sessions expire or auth is missing — not application bugs.
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof HttpError) {
    if (UNAUTHORIZED_ERROR_NAMES.has(error.name)) {
      return true;
    }
    if (
      typeof error.code === 'number' &&
      UNAUTHORIZED_STATUS_CODES.has(error.code)
    ) {
      return true;
    }
    return false;
  }

  if (error !== null && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    if (typeof obj['code'] === 'number' && obj['code'] === 401) {
      return true;
    }
    if (obj['name'] === 'Unauthorized') {
      return true;
    }
  }

  let raw: string;
  if (error instanceof Error) {
    raw = error.message;
  } else if (error !== null && typeof error === 'object') {
    raw = JSON.stringify(error);
  } else {
    raw = String(error ?? '');
  }

  const msg = raw.toLowerCase();
  return UNAUTHORIZED_PATTERNS.some((pattern) => msg.includes(pattern));
}

/**
 * Reasons that mean the user's *session* itself is gone/invalid, so the app
 * should proactively sign the user out and prompt re-authentication.
 *
 * Deliberately excludes `invalid-token` (integration auth, not a user session)
 * and `unknown` (e.g. a permission failure that surfaced as 401) so we never
 * sign a user out for an authorization problem they could otherwise recover
 * from.
 */
const SESSION_LOSS_REASONS = new Set<UnauthorizedReason>([
  'no-session',
  'session-expired',
  'invalid-session',
]);

/**
 * Returns `true` when an unauthorized error is caused by the user's session
 * being absent/expired/invalid. These are the cases where the app should
 * auto sign-out rather than surfacing a raw error screen.
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (!isUnauthorizedError(error)) {
    return false;
  }
  const reason = getUnauthorizedReason(error);
  return reason !== null && SESSION_LOSS_REASONS.has(reason);
}
